import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {useDispatch} from 'react-redux';
import * as reduxApi from 'src/utils/redux-api-v2';

import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import {
  Dialog,
  DialogBody,
  DialogHeader,
  DialogFooter,
} from '@spaced-out/ui-design-system/lib/components/Dialog';
import {
  Toast,
  TOAST_SEMANTIC,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  FileObject,
  FileUpload,
} from '@spaced-out/ui-design-system/lib/components/index';
import {
  SubTitleLarge,
  BodyLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';

import css from './attach-file.css';


type AttachFileProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (response: {
    id: number;
    filename: string;
    attachmentType: string;
    file: string;
  }) => void;
  onDeleteFile: (deletedFileId: number) => void;
  jobId: number;
  candidateId: number;
  title?: string;
  attachmentType?: string;
  initialFileName?: string;
  initialAttachmentType?: string;
  editFileId?: number;
  isEdit?: boolean;
};

const AttachFile = ({
  isOpen,
  onClose,
  onSubmit,
  onDeleteFile,
  jobId,
  candidateId,
  title,
  attachmentType = 'CANDIDATE',
  initialFileName = '',
  initialAttachmentType = '',
  editFileId = 0,
  isEdit = false,
}: AttachFileProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();

  const titleText = title ? title : t('Upload_Attachment', 'Upload Attachment');

  const [fileName, setFileName] = React.useState(initialFileName);
  const [selectedAttachmentType, setSelectedAttachmentType] = React.useState(
    initialAttachmentType,
  );
  const [file, setFile] = React.useState([]);

  const [isAttachingFile, setIsAttachingFile] = React.useState(false);
  const [isDeletingFile, setIsDeletingFile] = React.useState(false);

  const [
    isDeleteFileConfirmationModalOpen,
    setIsDeleteFileConfirmationModalOpen,
  ] = React.useState(false);

  const handleFileUpload = (filesArray: Array<FileObject>) => {
    // @ts-expect-error - TS2345 - Argument of type 'FileObject[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
    setFile(filesArray);
    setFileName(filesArray[0].file.name);
  };

  const clearFile = () => {
    setFile([]);
    setFileName('');
  };

  const ATTACHMENT_OPTIONS = {
    CANDIDATE: [
      {label: t('Contact_details', 'Contact details'), key: 'CONTACT'},
      {label: t('Resume', 'Resume'), key: 'RESUME'},
      {
        label: t('Work_experience', 'Work experience'),
        key: 'WORK_EXP_DOCUMENTS',
      },
      {label: t('Address_proof', 'Address proof'), key: 'ADDRESS_PROOF'},
      {label: t('Photo_ID_proof', 'Photo ID proof'), key: 'PHOTO_ID_PROOF'},
      {
        label: t('Educational_documents', 'Educational documents'),
        key: 'EDUCATION_DOCUMENTS',
      },
      {label: t('Others', 'Others'), key: 'OTHER'},
      {label: t('Candidate_photo', 'Candidate photo'), key: 'CANDIDATE_PHOTO'},
    ],
    APPLICATION: [
      {label: t('Resume', 'Resume'), key: 'RESUME'},
      {label: t('Review', 'Review'), key: 'REVIEW'},
      {label: t('Offer', 'Offer'), key: 'OFFER'},
      {label: t('Others', 'Others'), key: 'OTHER'},
    ],
  } as const;

  const handleAttachFile = async () => {
    setIsAttachingFile(true);

    const formData = new FormData();

    // @ts-expect-error - TS2339 - Property 'file' does not exist on type 'never'.
    formData.append('file', file[0].file);

    if (attachmentType === 'APPLICATION') {
      formData.append('jobId', jobId.toString());
    }
    formData.append('filename', fileName);
    formData.append('attachmentType', selectedAttachmentType);

    const endpoint = isEdit
      ? `api/attachments/${editFileId}`
      : `api/candidates/${candidateId}/attach`;

    try {
      const response = await dispatch(
        reduxApi.post(endpoint, formData, {}, {apiPath: '/trmapi/'}),
      );

      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.success}>
          <ToastTitle>
            {isEdit
              ? `${t(
                  'File_Updated_Successfully',
                  'File Updated Successfully',
                )}!`
              : `${t(
                  'File_Attached_Successfully',
                  'File Attached Successfully',
                )}!`}
          </ToastTitle>
        </Toast>,
      );

      onSubmit(response);
    } catch (error: any) {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>
            {isEdit
              ? `${t('File_Update_Failed', 'File Update Failed')}!`
              : `${t('File_Attachment_Failed', 'File Attachment Failed')}!`}
          </ToastTitle>
        </Toast>,
      );
    } finally {
      setIsAttachingFile(false);
    }
  };

  const handleConfirmDeleteFile = async () => {
    setIsDeletingFile(true);

    try {
      await dispatch(
        reduxApi.post(
          `api/attachments/${editFileId}/delete`,
          {},
          {},
          {apiPath: '/trmapi/'},
        ),
      );

      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.success}>
          <ToastTitle>{`${t(
            'File_Deleted_Successfully',
            'File Deleted Successfully',
          )}!`}</ToastTitle>
        </Toast>,
      );
      setIsDeleteFileConfirmationModalOpen(false);
      onDeleteFile(editFileId);
    } catch (error: any) {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>{`${t(
            'Unable_to_delete_file',
            'Unable to delete file',
          )}!`}</ToastTitle>
        </Toast>,
      );
    } finally {
      setIsDeletingFile(false);
    }
  };

  const isSubmitDisabled =
    !fileName ||
    !selectedAttachmentType ||
    (!isEdit && file.length === 0) ||
    (isEdit &&
      fileName === initialFileName &&
      selectedAttachmentType === initialAttachmentType);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        initialFocus={1}
        classNames={{content: css.attachFileModalContent}}
      >
        <ModalHeader onCloseButtonClick={onClose}>
          <SubTitleLarge>
            {isEdit ? t('Edit_Attachment', 'Edit Attachment') : titleText}
          </SubTitleLarge>
        </ModalHeader>

        <ModalBody className={css.attachFileModalBody}>
          <FileUpload
            maxFiles={1}
            secondaryInstruction={t('File_type', 'File type')}
            accept={{'*': ['*']}}
            onValidFilesDrop={handleFileUpload}
            onFileClear={clearFile}
          />

          <Input
            label={t('File_Name', 'File Name')}
            value={fileName}
            required
            type="text"
            onChange={(e) => setFileName(e.target.value)}
          />

          <SimpleDropdown
            label={t('Attachment_Type', 'Attachment Type')}
            required
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly CANDIDATE: readonly [{ readonly label: string; readonly key: "CONTACT"; }, { readonly label: string; readonly key: "RESUME"; }, { readonly label: string; readonly key: "WORK_EXP_DOCUMENTS"; }, ... 4 more ..., { ...; }]; readonly APPLICATION: readonly [...]; }'.
            options={ATTACHMENT_OPTIONS[attachmentType]}
            // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'ChangeEvent<HTMLInputElement> | MenuOption'.
            onChange={(option) => setSelectedAttachmentType(option.key)}
            selectedKeys={[selectedAttachmentType]}
          />
        </ModalBody>

        <ModalFooter classNames={{actions: css.attachFileModalFooter}}>
          {isEdit ? (
            <Button
              iconLeftName="trash"
              onClick={() => setIsDeleteFileConfirmationModalOpen(true)}
              classNames={{
                wrapper: css.trashButton,
                icon: css.trashIcon,
              }}
            />
          ) : null}
          <div className={css.attachFileModalFooterButtons}>
            <Button type="ghost" onClick={onClose}>
              {t('Cancel', 'Cancel')}
            </Button>
            <Button
              type="primary"
              disabled={isSubmitDisabled}
              onClick={handleAttachFile}
              isLoading={isAttachingFile}
            >
              {isEdit ? t('Update', 'Update') : t('Save', 'Save')}
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      <Dialog
        isOpen={isDeleteFileConfirmationModalOpen}
        onClose={() => setIsDeleteFileConfirmationModalOpen(false)}
        semantic="neutral"
      >
        <DialogHeader>
          <SubTitleLarge>{t('Delete_File', 'Delete File')}</SubTitleLarge>
        </DialogHeader>

        <DialogBody className={css.stageChangeModalBody}>
          <BodyLarge color="secondary">{`${t(
            'Are_you_sure_you_want_to_delete_this_file',
            'Are you sure you want to delete this file',
          )}?`}</BodyLarge>
        </DialogBody>

        <DialogFooter>
          <Button
            isFluid
            type="tertiary"
            onClick={() => setIsDeleteFileConfirmationModalOpen(false)}
          >
            {t('Cancel', 'Cancel')}
          </Button>
          <Button
            isFluid
            type="secondary"
            isLoading={isDeletingFile}
            onClick={handleConfirmDeleteFile}
          >
            {t('Confirm', 'Confirm')}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default AttachFile;
